import { useMutation } from '@tanstack/react-query';
import { v4 as uuid } from 'uuid';
import { ASSIST_API, requestParams } from 'services/assist-api/axios';
import type { AssistQuery } from 'services/assist-api/query';
import { AssistQueryClient, useQueryAssist } from 'services/assist-api/query-client';
import { prepareCustomerUser } from './customers-users.prepare';
import { generateIdentifier } from './helpers';
import type { BaseCustomerUser } from './types';

const order = { 'order:[confirmed]': 'desc', 'order:[user.lastname]': 'desc' };
export const groups = [
  'customersusers-identifier',
  'customersusers-externalid',
  'customersusers-user',
  'customersusers-confirmed',
  'customersusers-status',
  'customersusers-level',
  'customersusers-groups',
  'customersusers-supervisorgroups',
  'customersusers-roles',
  'user-username',
  'user-email',
  'user-firstname',
  'user-lastname',
  'user-gender',
  'user-ethnicities',
  'user-status',
  'groupsusers-group',
  'groupssupervisors-group',
  'role-name',
  'customer-details'
];
export const include = 'user,groups,supervisorGroups,roles,customer';

/**
 * Fetch customer-users
 * @param params can be used to customise/limit the  customer-users retrieved
 * @returns the requested customer-users
 */
export const getCustomerUsers: GetMany<BaseCustomerUser> = async (params) =>
  (
    await ASSIST_API.get('customers-users', {
      params: requestParams({ order, groups, include }, params)
    })
  ).data;

/**
 * Fetch customer-user (singular)
 * @param params can be used to customise/limit the customer-user retrieved
 * @returns a single customer-user
 */
export const getCustomerUser: GetOne<BaseCustomerUser> = async (id, params) =>
  (
    await ASSIST_API.get(`customers-users/${id}`, {
      params: requestParams({ order, groups, include }, params)
    })
  ).data;

/**
 * Hook implementation to fetch customer-users
 *  - caches data
 *  - loading, error, etc
 * @param params additional options to filter the request
 * @returns customer-users matching request
 */
export const useCustomerUsers: AssistQuery.UseMany<BaseCustomerUser> = (params, options) =>
  useQueryAssist(['customers-users', params], () => getCustomerUsers(params), options);

/**
 * Given an customer-user id fetch and return the area
 *  - only runs once customer-user id is provided
 *  - fetches the one customer-user
 *  - caches data
 * @param id customer-user id
 * @param params optional parameters to change requested data
 * @returns the matching customer-user
 */
export const useCustomerUser: AssistQuery.UseOne<BaseCustomerUser> = (id, params, options) =>
  useQueryAssist(['customers-users', { id, ...params }], () => getCustomerUser(id, params), {
    ...options,
    enabled: !!id
  });

/**
 * Creates methods to send DELETE requests to the api
 * @param options ReactQuery mutation options
 * @returns useMutation
 */
export const useDeleteCustomerUser: AssistQuery.UseMutation<BaseCustomerUser, number> = (options) =>
  useMutation((id) => ASSIST_API.delete(`customers-users/${id}`), {
    ...options,
    onSettled: (...args) => {
      AssistQueryClient.invalidateQueries(['customers-users']);
      if (options?.onSettled) {
        options.onSettled(...args);
      }
    }
  });

export type CustomerUserPatch = Pick<
  Partial<BaseCustomerUser>,
  'id' | 'customer' | 'identifier' | 'confirmed' | 'roles' | 'externalId' | 'level'
> & {
  user: {
    id: number;
    lastname?: string;
    firstname?: string;
    username?: string;
    email?: string;
  };
};

/**
 * Creates methods to send PATCH requests to the api
 * @param options ReactQuery mutation options
 * @returns useMutation
 */
export const usePatchCustomerUser: AssistQuery.UseMutation<BaseCustomerUser, CustomerUserPatch> = (
  options
) =>
  useMutation(
    ({ id, user, customer, identifier, confirmed, roles, externalId, level }: CustomerUserPatch) =>
      ASSIST_API.patch(
        `customers-users/${id}`,
        prepareCustomerUser(
          {
            identifier,
            externalId,
            confirmed,
            level,
            customer: customer?.id,
            roles: roles?.map((role) => role.id)
          },
          user
        ),
        { params: requestParams({ order, groups, include }) }
      ),
    {
      ...options,
      onSettled: (...args) => {
        AssistQueryClient.invalidateQueries(['customers-users']);
        if (options?.onSettled) {
          options.onSettled(...args);
        }
      }
    }
  );

export type CustomerUserPost = Pick<
  Partial<BaseCustomerUser>,
  'identifier' | 'externalId' | 'level' | 'roles'
> & {
  customer: number;
  user: {
    lastname?: string;
    firstname?: string;
    username?: string;
    email?: string;
    password?: string;
    confirmed?: boolean;
    status?: string;
    gender?: API_JSON;
    ethnicities?: API_JSON[];
  };
};

/**
 * Creates methods to send PATCH requests to the api
 * @param options ReactQuery mutation options
 * @returns useMutation
 */
export const usePostCustomerUser: AssistQuery.UseMutation<BaseCustomerUser, CustomerUserPost> = (
  options
) =>
  useMutation(
    ({ user, customer, identifier, roles, externalId, level }: CustomerUserPost) =>
      ASSIST_API.post(
        `customers-users`,
        prepareCustomerUser(
          {
            identifier: identifier || generateIdentifier(customer, uuid()),
            externalId,
            confirmed: true,
            level,
            customer,
            roles: roles?.map((role) => role.id)
          },
          user
        ),
        { params: requestParams({ order, groups, include }) }
      ),
    {
      ...options,
      onSettled: (...args) => {
        AssistQueryClient.invalidateQueries(['customers-users']);
        if (options?.onSettled) {
          options.onSettled(...args);
        }
      }
    }
  );

type LinkCustomerUserVariables = {
  user: number;
  customer: number;
  identifier?: string;
};

/**
 * Links an existing `user` with a `customer`. If no `identifier` is supplied one will be generated
 * @param options ReactQuery mutation options
 * @returns useMutation
 */
export const useLinkCustomerUser: AssistQuery.UseMutation<
  BaseCustomerUser,
  LinkCustomerUserVariables
> = (options) =>
  useMutation(
    ({ user, customer, identifier }: LinkCustomerUserVariables) =>
      ASSIST_API.post(
        'customers-users',
        {
          data: {
            type: 'CustomersUsers',
            attributes: { identifier: identifier || generateIdentifier(customer, user) },
            relationships: {
              user: { data: { id: `/api/v2/users/${user}`, type: 'User' } },
              customer: { data: { id: `/api/v2/customers/${customer}`, type: 'Customer' } }
            }
          }
        },
        { params: requestParams({}) }
      ),
    {
      ...options,
      onSettled: (...args) => {
        AssistQueryClient.invalidateQueries(['customers-users']);
        if (options?.onSettled) {
          options.onSettled(...args);
        }
      }
    }
  );

export const getCustomerUserForOnboard = (
  customer: number,
  user: number,
  params?,
  options?
): Promise<BaseCustomerUser> =>
  AssistQueryClient.fetchQuery(
    ['customers-users', { ...params, filters: { customer, user } }],
    async () =>
      ((await getCustomerUsers({ ...params, filters: { customer, user } }))?.json || [])[0],
    { ...options, enabled: !!customer && !!user }
  );
